import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ChannelCard } from "@/components/connect/ChannelCard";
import { EmailChannelCard } from "@/components/connect/EmailChannelCard";
import { ComposerDialog } from "@/components/connect/ComposerDialog";
import { CommunicationTimeline } from "@/components/connect/CommunicationTimeline";
import { rc, CHANNEL_SETUP_REQUIREMENTS } from "@/lib/connect";
import { logEvent } from "@/lib/analytics";
import { Coins, Plus, ChatCircleDots, PaperPlaneTilt, CheckCircle, PaintBrush, Article, ClockCountdown } from "@phosphor-icons/react";

const CAT_ICON = {
  Communications: ChatCircleDots, "Account Updates": Plus, AI: Coins, Automation: PaperPlaneTilt, Other: Coins,
};
const CH_TITLE = { whatsapp: "WhatsApp", email: "Email", sms: "SMS", voice: "Voice" };

export default function Connect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [summary, setSummary] = useState(null);
  const [needsTenant, setNeedsTenant] = useState(false);
  const [composer, setComposer] = useState({ open: false, account: null });
  const [setup, setSetup] = useState({ open: false, channel: null });

  const load = useCallback(async () => {
    try {
      const [ch, sm] = await Promise.all([
        api.get("/connect/channels"),
        api.get("/connect/summary"),
      ]);
      if (ch.data?.needs_tenant || sm.data?.needs_tenant) { setNeedsTenant(true); return; }
      setNeedsTenant(false);
      setChannels(ch.data?.channels || []);
      setSummary(sm.data || null);
    } catch (e) {
      setChannels([]); setSummary(null);
    }
  }, []);

  useEffect(() => { load(); logEvent("connect_viewed", {}); }, [load]);

  // Keep RC/free-comms + timeline fresh after a send or usage change.
  useEffect(() => {
    const reload = () => load();
    window.addEventListener("recoveros:usage-changed", reload);
    window.addEventListener("recoveros:comms-changed", reload);
    return () => {
      window.removeEventListener("recoveros:usage-changed", reload);
      window.removeEventListener("recoveros:comms-changed", reload);
    };
  }, [load]);

  // Deep link from "Add Account → Start Follow-up": /connect?account=<id>&compose=1
  useEffect(() => {
    const acc = params.get("account");
    if (acc && params.get("compose") === "1") {
      api.get(`/accounts/${acc}`)
        .then((r) => setComposer({ open: true, account: r.data || null }))
        .catch(() => setComposer({ open: true, account: null }));
    }
  }, [params]);

  const statusOf = (channel) => channels.find((c) => c.channel === channel)?.status || "not_connected";
  const connectedCount = channels.filter((c) => c.status === "connected").length;
  const readyChannels = channels.filter((c) => c.status === "connected").map((c) => c.channel);
  const totalChannels = 4;

  const openComposer = (account = null) => setComposer({ open: true, account });
  const addAccount = () => window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source: "connect" } }));

  const onChannelAction = (channel) => {
    if (channel === "voice") return; // not interactive yet
    if (channel === "email" && readyChannels.includes("email")) { openComposer(null); return; }
    logEvent("connect_channel_setup_opened", { channel });
    setSetup({ open: true, channel });
  };

  if (needsTenant) {
    return (
      <div data-testid="connect-page">
        <div className="mb-6 border-b border-border pb-4">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">RecoverOS Connect</h1>
          <p className="text-sm text-muted-foreground mt-1">One Dashboard. Every Follow-up.</p>
        </div>
        <Empty text="Select a tenant from the switcher above to view its Connect workspace." />
      </div>
    );
  }

  const balMilli = summary?.balance_milli ?? 0;
  const usedMilli = summary?.used_this_month_milli ?? 0;
  const breakdown = summary?.breakdown || [];
  const lifetimeFree = !!summary?.lifetime_free;
  const freeComms = summary?.free_comms || { total: 0, used: 0, remaining: 0 };

  return (
    <div data-testid="connect-page">
      {/* Header */}
      <div className="mb-6 border-b border-border pb-4 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">RecoverOS Connect</h1>
          <p className="text-sm text-muted-foreground mt-1">One Dashboard. Every Follow-up.</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-muted-foreground tracking-wide">WhatsApp • Email • SMS • Voice</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-sm bg-secondary" data-testid="connect-channels-summary">
              <CheckCircle size={13} weight="bold" className={connectedCount > 0 ? "text-emerald-600" : "text-muted-foreground"} />
              {connectedCount} of {totalChannels} channels connected
            </span>
          </div>
        </div>
        <Button className="rounded-sm" onClick={() => openComposer(null)} data-testid="connect-start-followup-primary">
          <Plus size={16} weight="bold" className="mr-1.5" /> Start a Follow-up
        </Button>
      </div>

      {/* Email Studio quick nav */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="connect-email-studio-nav">
        <span className="text-xs font-semibold text-muted-foreground mr-1">Email Control Layer:</span>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => window.location.assign("/connect/email/branding")} data-testid="nav-email-branding"><PaintBrush size={14} weight="bold" className="mr-1.5" />Branding</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => window.location.assign("/connect/templates/email")} data-testid="nav-email-templates"><Article size={14} weight="bold" className="mr-1.5" />Templates</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => window.location.assign("/connect/email/rules")} data-testid="nav-email-rules"><ClockCountdown size={14} weight="bold" className="mr-1.5" />Rules & Frequency</Button>
      </div>

      {/* Channel cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="connect-channels">
        <ChannelCard channel="whatsapp" status={statusOf("whatsapp")} onAction={onChannelAction} />
        <EmailChannelCard />
        <ChannelCard channel="sms" status={statusOf("sms")} onAction={onChannelAction} />
        <ChannelCard channel="voice" status={statusOf("voice")} onAction={onChannelAction} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* RC summary */}
        <div className="border border-border bg-card p-4 rounded-sm" data-testid="connect-rc-summary">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Coins size={18} weight="bold" className="text-[#16A34A]" />
              <h2 className="font-heading font-bold">RecoverOS Credits</h2>
            </div>
            {lifetimeFree && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="connect-lifetime-free-badge">
                <CheckCircle size={12} weight="bold" /> Lifetime Free
              </span>
            )}
          </div>

          {lifetimeFree ? (
            <div className="mt-3 rounded-sm border border-emerald-200 bg-emerald-50 p-3" data-testid="connect-lifetime-free-note">
              <div className="text-sm font-semibold text-emerald-900">Communications are on us</div>
              <p className="text-xs text-emerald-800 mt-0.5">Your workspace has a Lifetime Free entitlement — send communications without using RecoverOS Credits. No recharge required.</p>
            </div>
          ) : (
            <div className="mt-3 rounded-sm border border-border bg-secondary/40 p-3" data-testid="connect-free-comms">
              <div className="flex items-center justify-between">
                <span className="label-caps">Free Communications</span>
                <span className="text-sm font-bold mono" data-testid="connect-free-comms-remaining">{freeComms.remaining} of {freeComms.total} left</span>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-border overflow-hidden">
                <div className="h-full bg-[#16A34A] transition-all" style={{ width: `${freeComms.total ? Math.round((freeComms.remaining / freeComms.total) * 100) : 0}%` }} />
              </div>
              {freeComms.remaining === 0 && (
                <p className="text-[11px] text-muted-foreground mt-1.5">Your free communications are used up. Use RecoverOS Credits or recharge to keep sending.</p>
              )}
            </div>
          )}

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="label-caps">Current Balance</div>
              <div className="text-2xl font-black font-heading mono mt-1" data-testid="connect-rc-balance">{rc(balMilli)}</div>
            </div>
            <div>
              <div className="label-caps">Used This Month</div>
              <div className="text-2xl font-black font-heading mono mt-1" data-testid="connect-rc-used">{rc(usedMilli)}</div>
            </div>
          </div>
          <div className="mt-3">
            <Button size="sm" className="rounded-sm gap-1.5 bg-[#16A34A] hover:bg-[#138a3e]" onClick={() => navigate("/subscription#recharge-rc")} data-testid="connect-recharge-btn">
              <Plus size={16} weight="bold" /> Recharge RC
            </Button>
          </div>
          <div className="mt-4">
            <div className="label-caps mb-2">Credits Used by Action</div>
            {breakdown.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="connect-rc-breakdown-empty">No usage yet this month.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="connect-rc-breakdown">
                {breakdown.map((b) => {
                  const I = CAT_ICON[b.category] || Coins;
                  return (
                    <li key={b.category} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground"><I size={14} weight="bold" /> {b.category}</span>
                      <span className="mono font-semibold">{rc(b.milli)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Recent communications */}
        <div className="border border-border bg-card p-4 rounded-sm lg:col-span-2" data-testid="connect-recent">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-heading font-bold">Recent Communications</h2>
            <Button size="sm" className="rounded-sm" onClick={() => openComposer(null)} data-testid="connect-new-followup-btn">
              <Plus size={15} weight="bold" className="mr-1" /> Start a Follow-up
            </Button>
          </div>
          <CommunicationTimeline
            limit={30}
            emptyTitle="Start your first customer follow-up"
            emptyText="Add an account or select an existing customer to start managing payment communications from RecoverOS."
            emptyActions={
              <>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={addAccount} data-testid="connect-empty-add-account">
                  <Plus size={14} weight="bold" className="mr-1" /> Add Account
                </Button>
                <Button size="sm" className="rounded-sm" onClick={() => openComposer(null)} data-testid="connect-empty-start-followup">
                  Start Follow-up
                </Button>
              </>
            }
          />
        </div>
      </div>

      {/* Contextual help */}
      <div className="mt-4 border border-dashed border-border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="connect-help">
        <div className="flex items-center gap-2 text-sm">
          <ChatCircleDots size={18} weight="bold" className="text-primary" />
          <span className="text-muted-foreground">Need help setting up communications?</span>
        </div>
        <Button variant="outline" size="sm" className="rounded-sm"
          onClick={() => window.dispatchEvent(new Event("recoveros:open-chat"))} data-testid="connect-help-chat">
          Chat with RecoverOS
        </Button>
      </div>

      {/* Channel setup dialog — reveals requirements only after the CTA is clicked. No activation is simulated. */}
      <Dialog open={setup.open} onOpenChange={(o) => setSetup((s) => ({ ...s, open: o }))}>
        <DialogContent className="rounded-sm max-w-md" data-testid="connect-setup-dialog">
          <DialogHeader>
            <DialogTitle>Set up {CH_TITLE[setup.channel] || ""}</DialogTitle>
            <DialogDescription>What’s needed before this channel can send real messages.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="connect-setup-requirements">
            {setup.channel ? CHANNEL_SETUP_REQUIREMENTS[setup.channel] : ""}
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setSetup({ open: false, channel: null })} data-testid="connect-setup-close">Close</Button>
            <Button className="rounded-sm" onClick={() => { setSetup({ open: false, channel: null }); window.dispatchEvent(new Event("recoveros:open-chat")); }} data-testid="connect-setup-chat">
              Chat with RecoverOS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ComposerDialog open={composer.open} account={composer.account}
        readyChannels={readyChannels} emailRateMilli={summary?.email_rate_milli || 0}
        onOpenChange={(o) => setComposer((s) => ({ ...s, open: o }))} />
    </div>
  );
}
